import type { AmbientZoneRef } from '../scene/SceneManifest';
import type { AudioEngine } from './AudioEngine';
import type { AudioSource } from './AudioSource';

/**
 * Per-frame controller that watches the listener's world XZ position, picks
 * the highest-priority `AmbientZoneRef` polygon that contains it, and either:
 *
 *  - **Bed mode** (`spawnIntervalSec` null or 0): keeps a looping
 *    `AudioSource` per zone, crossfades on zone change.
 *  - **Spawn mode** (`spawnIntervalSec > 0`): fires one-shots at a jittered
 *    cadence while the listener is inside the zone, each at a random angle
 *    around the listener at 8–25 m so birds/insects feel directional. Random
 *    variant is picked automatically by `playOneShot`.
 *
 * Bed sources are kept alive across re-entry; spawn timers reset on every
 * enter so a quick out-and-in doesn't fire a stale-scheduled call.
 *
 * Bed-source creation requires the clip to already be cached in
 * `audio.library` (via `acquire`). If it isn't, the controller leaves
 * `activeZoneId` unchanged and retries on the next tick — so callers don't
 * have to await preload before constructing.
 */
export class AmbientZoneController {
  private readonly bedSourcesByZone = new Map<string, AudioSource>();
  /** Zone id whose source is currently faded in (volume > 0). Null when none. */
  private activeZoneId: string | null = null;
  /** Sorted descending by priority once, reused per tick. */
  private readonly zonesByPriority: AmbientZoneRef[];

  /** Wall-clock ms at which the active spawn zone should fire its next one-shot. */
  private nextSpawnAtMs: number | null = null;
  /**
   * Spawn radius around the listener, in meters. Sized for clips authored
   * with refDistance ~10 m and rolloffFactor ~0.5 (gain ~100 % at 10 m,
   * ~57 % at 25 m): wide enough that the panner gives a clear directional
   * cue without making subtler variants drop below audibility.
   */
  private readonly spawnRadius: { min: number; max: number } = { min: 8, max: 25 };

  constructor(
    private readonly audio: AudioEngine,
    zones: readonly AmbientZoneRef[],
    private readonly fadeSeconds: number = 1.0,
  ) {
    this.zonesByPriority = [...zones].sort((a, b) => b.priority - a.priority);
  }

  /**
   * Called every frame from the scene tick. `x`/`y`/`z` is the listener's
   * world position (Y-up). `y` is used as the height for spawn-mode one-shot
   * positions so birds fire at the listener's height, not at ground 0.
   */
  tick(x: number, y: number, z: number): void {
    if (this.zonesByPriority.length === 0) return;
    const targetId = this.findActiveZoneId(x, z);

    if (targetId !== this.activeZoneId) {
      this.onZoneChange(targetId);
    }

    // Spawn mode tick: only fire when the listener is *currently* inside the
    // committed zone's polygon. Without this gate, a stale `nextSpawnAtMs`
    // (e.g. left over after a failed bed-zone transition) would fire the old
    // zone's one-shots at the listener's new position.
    if (
      this.activeZoneId &&
      this.activeZoneId === targetId &&
      this.nextSpawnAtMs !== null &&
      performance.now() >= this.nextSpawnAtMs
    ) {
      const zone = this.findZoneById(this.activeZoneId);
      if (zone) this.fireSpawn(zone, x, y, z);
      this.nextSpawnAtMs = performance.now() + this.jitteredIntervalMs(this.activeZoneId);
    }
  }

  /** Tear down every source. Called on scene deactivate. */
  destroy(): void {
    for (const source of this.bedSourcesByZone.values()) source.destroy();
    this.bedSourcesByZone.clear();
    this.activeZoneId = null;
    this.nextSpawnAtMs = null;
  }

  private onZoneChange(targetId: string | null): void {
    if (!targetId) {
      this.fadePrevious();
      this.nextSpawnAtMs = null;
      this.activeZoneId = null;
      return;
    }

    const zone = this.findZoneById(targetId);
    if (!zone) {
      this.fadePrevious();
      this.nextSpawnAtMs = null;
      this.activeZoneId = null;
      return;
    }

    if (isSpawnZone(zone)) {
      this.fadePrevious();
      // First fire is jittered too — avoids a synchronized "bird call exactly
      // when you cross the boundary" tell.
      this.nextSpawnAtMs = performance.now() + this.jitteredIntervalMs(targetId);
      this.activeZoneId = targetId;
      return;
    }

    // Bed mode: ensure the new source FIRST. Only fade the previous bed and
    // commit the transition once we know it will succeed — otherwise leave
    // both `activeZoneId` and the previous source's volume untouched so the
    // old bed keeps playing and this path retries next tick.
    if (this.ensureBedSource(zone)) {
      this.fadePrevious();
      this.nextSpawnAtMs = null;
      this.activeZoneId = targetId;
    }
  }

  private fadePrevious(): void {
    if (this.activeZoneId) {
      this.bedSourcesByZone.get(this.activeZoneId)?.setVolume(0, this.fadeSeconds);
    }
  }

  private findActiveZoneId(x: number, z: number): string | null {
    for (const zone of this.zonesByPriority) {
      if (pointInPolygon(x, z, zone.polygon)) return zone.id;
    }
    return null;
  }

  private findZoneById(id: string): AmbientZoneRef | undefined {
    return this.zonesByPriority.find((z) => z.id === id);
  }

  /** Returns true when a bed source for this zone is live (created now or already running). */
  private ensureBedSource(zone: AmbientZoneRef): boolean {
    let source = this.bedSourcesByZone.get(zone.id);
    if (!source) {
      const created = this.audio.createSourceFromCached(zone.clipId, {
        bus: 'ambient',
        volume: 0, // ramped up below
        loop: true,
      });
      if (!created) return false;
      source = created;
      this.bedSourcesByZone.set(zone.id, source);
      source.play();
    }
    source.setVolume(zone.volume ?? 1, this.fadeSeconds);
    return true;
  }

  private fireSpawn(zone: AmbientZoneRef, x: number, y: number, z: number): void {
    // Random angle around listener; random radius in [min, max]. The clip's
    // own spatial config drives distance attenuation via the PannerNode.
    const angle = Math.random() * Math.PI * 2;
    const r =
      this.spawnRadius.min + Math.random() * (this.spawnRadius.max - this.spawnRadius.min);
    const px = x + Math.cos(angle) * r;
    const pz = z + Math.sin(angle) * r;
    this.audio.playOneShot(zone.clipId, {
      bus: 'ambient',
      volume: zone.volume ?? 1,
      position: [px, y, pz],
    });
  }

  /** plus or minus 50% jitter around the mean interval, in milliseconds. */
  private jitteredIntervalMs(zoneId: string): number {
    const zone = this.findZoneById(zoneId);
    const mean = zone?.spawnIntervalSec ?? 0;
    if (mean <= 0) return Number.POSITIVE_INFINITY;
    const jitterScale = 0.5 + Math.random();
    return mean * jitterScale * 1000;
  }
}

function isSpawnZone(zone: AmbientZoneRef): boolean {
  return (zone.spawnIntervalSec ?? 0) > 0;
}

/** Standard even-odd point-in-polygon. Polygon is a closed ring of [x, z] pairs. */
export function pointInPolygon(x: number, z: number, vertices: readonly number[][]): boolean {
  let inside = false;
  for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i++) {
    const [xi, zi] = vertices[i];
    const [xj, zj] = vertices[j];
    const intersects = zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}
